import React, { useCallback, useMemo } from 'react';
import { Modal } from 'antd';
import { closeModal } from './modalSlice';
import { ActionsBar, Button, Column, DataTable, Text, YColumnsType } from '@ydistri/ds';
import { TablePaginationConfig } from 'antd/lib';
import { useMainScope } from '../Configuration/ConfigurationStoresAndDepartments/useMainScope';
import {
  transformToConfigurationRule,
  usePutConfigurationRuleMutation,
} from '../Configuration/ConfigurationStoresAndDepartments/apiScopes';
import { useTemplateOrCalculation } from '../../hooks/useTemplateOrCalculation';
import { ConfigurationRuleScopeMode, StoreResponse } from '@ydistri/api-sdk';
import cloneDeep from 'lodash.clonedeep';
import { ColumnsType } from 'antd/es/table';
import {
  EntityColumns,
  EntityColumnsConfig,
  useStoreTableColumnsProvider,
} from '../../hooks/useEntityTableColumnsProvider';
import { useAppDispatch } from '../../store';
import useIsConfigurationEditable from '../../hooks/useIsConfigurationEditable';
import { computeRemSize } from '@ydistri/theme';
import { MdWarning } from 'react-icons/md';
import { useStoresReport } from '../Configuration/ConfigurationStoresAndDepartments/useStoresReport';
import { flaggedStores } from '../Configuration/ConfigurationStoresAndDepartments/storesReportLib';
import { useGetStoresQuery } from '../../apis/apiLists';
import { useTemplateOrCalculationWithValue } from '../../hooks/useTemplateOrCalculationWithValue';

interface StoreSaleInfoTableRow {
  store: Pick<StoreResponse, 'customerStoreId' | 'name' | 'code'>;
  storeTypeName: string;
  regionName: string;
  reason: string;
  firstSaleOn: string;
  lastSaleOn: string;
}

/**
 * The stores the server flagged in the general scope - no recent sale or a first sale too recent -
 * with the reason it gave, and one action to exclude them all.
 */
const StoreSaleInfoModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const mainScope = useMainScope();
  const templateOrCalculation = useTemplateOrCalculation();
  const templateOrCalculationWithValue = useTemplateOrCalculationWithValue();
  const isConfigurationEditable = useIsConfigurationEditable();

  const { problems, isLoading } = useStoresReport();
  const { data: availableStores, isFetching: isFetchingStores } = useGetStoresQuery(
    templateOrCalculationWithValue.value,
  );
  const [putConfigurationRuleMutation] = usePutConfigurationRuleMutation();

  const storeColumnsProvider = useStoreTableColumnsProvider();

  const flagged = useMemo(() => flaggedStores(problems), [problems]);

  const data: StoreSaleInfoTableRow[] = useMemo(
    () =>
      flagged.map(f => {
        const s = availableStores?.object[f.id];
        return {
          store: {
            customerStoreId: s?.customerStoreId ?? '',
            code: s?.code ?? '',
            name: s?.name ?? `Store ${f.id}`,
            id: f.id,
          },
          storeTypeName: s?.storeType?.name ?? '',
          regionName: s?.region?.name ?? '',
          reason: f.message,
          firstSaleOn: f.data.firstSaleOn ?? '',
          lastSaleOn: f.data.lastSaleOn ?? '',
        };
      }),
    [availableStores?.object, flagged],
  );

  const handleClose = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const handleExclude = useCallback(() => {
    const excludeIds = flagged.map(f => f.id);

    if (mainScope) {
      const scope = cloneDeep(mainScope);

      switch (scope.entities.Stores.selectionOption) {
        case ConfigurationRuleScopeMode.All: {
          scope.entities.Stores.selectionOption = ConfigurationRuleScopeMode.AllExcept;
          scope.entities.Stores.selectedIds = excludeIds;
          break;
        }
        case ConfigurationRuleScopeMode.AllExcept: {
          excludeIds.forEach(id => {
            if (!scope.entities.Stores.selectedIds.includes(id)) {
              scope.entities.Stores.selectedIds.push(id);
            }
          });
          break;
        }
        case ConfigurationRuleScopeMode.Specified: {
          scope.entities.Stores.selectedIds = scope.entities.Stores.selectedIds.filter(
            s => !excludeIds.includes(s),
          );
          break;
        }
      }

      const configurationRule = transformToConfigurationRule(scope);

      putConfigurationRuleMutation({
        id: scope.id,
        templateId: templateOrCalculation.id,
        data: configurationRule,
      });
    }
  }, [putConfigurationRuleMutation, mainScope, flagged, templateOrCalculation.id]);

  const footer = useMemo(
    () => (
      <ActionsBar>
        <Button
          key="exclude"
          onClick={handleExclude}
          $colorVariant="ghost"
          disabled={!isConfigurationEditable}
        >
          Exclude all from scope
        </Button>
        <Button key="close" onClick={handleClose}>
          Close
        </Button>
      </ActionsBar>
    ),
    [handleClose, handleExclude, isConfigurationEditable],
  );

  const columns: YColumnsType<StoreSaleInfoTableRow>[] = useMemo(() => {
    const storeColumnsConfig: EntityColumnsConfig<StoreSaleInfoTableRow> = {
      [EntityColumns.NAME]: {
        showSorterTooltip: false,
      },
      [EntityColumns.CODE]: {
        width: '8rem',
      },
      [EntityColumns.ID]: {
        width: computeRemSize(70),
      },
    };

    const cols: ColumnsType<StoreSaleInfoTableRow> =
      storeColumnsProvider<StoreSaleInfoTableRow>(storeColumnsConfig);

    cols.push({
      title: 'Why',
      dataIndex: 'reason',
      key: 'reason',
      showSorterTooltip: false,
      ellipsis: true,
      sorter: (a, b) => a.reason.localeCompare(b.reason),
      render: (value: string) => (
        <Text $type="warning">
          <MdWarning /> {value}
        </Text>
      ),
    });

    cols.push({
      title: 'First sale',
      dataIndex: 'firstSaleOn',
      key: 'firstSaleOn',
      showSorterTooltip: false,
      width: computeRemSize(120),
      sorter: (a, b) => a.firstSaleOn.localeCompare(b.firstSaleOn),
      render: (value: string) => (value === '' ? 'No sale info' : value),
    });

    cols.push({
      title: 'Last sale',
      dataIndex: 'lastSaleOn',
      key: 'lastSaleOn',
      showSorterTooltip: false,
      width: computeRemSize(120),
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.lastSaleOn.localeCompare(b.lastSaleOn),
      render: (value: string) => (value === '' ? 'No sale info' : value),
    });

    cols.push({
      title: 'Store type',
      dataIndex: 'storeTypeName',
      key: 'storeTypeName',
      showSorterTooltip: false,
      width: computeRemSize(170),
      ellipsis: true,
      sorter: (a, b) => a.storeTypeName.localeCompare(b.storeTypeName),
    });

    cols.push({
      title: 'Region',
      dataIndex: 'regionName',
      key: 'regionName',
      showSorterTooltip: false,
      width: computeRemSize(170),
      ellipsis: true,
      sorter: (a, b) => a.regionName.localeCompare(b.regionName),
    });

    return cols;
  }, [storeColumnsProvider]);

  const pagination: TablePaginationConfig = useMemo(
    () => ({
      showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} flagged stores`,
      pageSize: 15,
      showSizeChanger: false,
      hideOnSinglePage: true,
      size: 'default',
      position: ['bottomLeft'],
    }),
    [],
  );

  if (data.length === 0) return null;

  return (
    <Modal width="90%" title="Store sale info" onCancel={handleClose} open={true} footer={footer}>
      <Column $gap="1rem">
        <DataTable<StoreSaleInfoTableRow>
          showBorder={true}
          showHeader={true}
          id="store-configurations"
          loading={isLoading || isFetchingStores}
          columns={columns}
          dataSource={data}
          size="small"
          pagination={pagination}
        />
      </Column>
    </Modal>
  );
};

export default StoreSaleInfoModal;
